import { organizationsApi } from '@/entities/organizations/api';
export interface FrontlineOrganization {
  id: string;
  name: string;
  timezone: string;
  enabled: boolean;
  activePhoneCount: number;
}
export interface PhoneMapping {
  id: string;
  organizationId: string;
  phoneNumber: string;
  retellAgentId: string | null;
  label: string | null;
  isActive: boolean;
}
export interface Contact {
  userId: string;
  phoneNumber: string;
  acceptsEscalations: boolean;
  lastTestSentAt: string | null;
}
export interface Shift {
  id: string;
  primaryUserId: string;
  backupUserId: string | null;
  startsAt: string;
  endsAt: string;
}
export interface Member {
  id: string;
  firstName: string;
  lastName: string;
}
export interface FrontlineSetup extends FrontlineOrganization {
  phoneNumbers: PhoneMapping[];
  members: Member[];
  contacts: Contact[];
  webhookUrl: string;
  analysisSchema: unknown[];
  notifierAnalysisSchema: unknown[];
  escalationActivationConfigured: boolean;
}
interface Page {
  data: FrontlineOrganization[];
  current_page: number;
  last_page: number;
  total: number;
  per_page: number;
}
const api = organizationsApi.enhanceEndpoints({
  addTagTypes: ['FrontlineSetup']
}).injectEndpoints({
  endpoints: b => ({
    frontlineOrganizations: b.query<Page, {
      search: string;
      scope: 'frontline' | 'all';
      page: number;
    }>({
      query: params => ({
        url: '/frontline/organizations',
        params
      }),
      providesTags: ['FrontlineSetup']
    }),
    frontlineSetup: b.query<FrontlineSetup, string>({
      query: id => `/frontline/organizations/${id}`,
      providesTags: ['FrontlineSetup']
    }),
    frontlineShifts: b.query<{
      data: Shift[];
    }, {
      id: string;
      from: string;
      to: string;
    }>({
      query: ({
        id,
        ...params
      }) => ({
        url: `/frontline/organizations/${id}/shifts`,
        params
      }),
      providesTags: ['FrontlineSetup']
    }),
    updateFrontline: b.mutation<unknown, {
      path: string;
      method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      body?: Record<string, unknown>;
    }>({
      query: ({
        path,
        ...args
      }) => ({
        url: `/frontline/${path}`,
        ...args
      }),
      invalidatesTags: ['FrontlineSetup']
    })
  })
});
export const {
  useFrontlineOrganizationsQuery,
  useFrontlineSetupQuery,
  useFrontlineShiftsQuery,
  useUpdateFrontlineMutation
} = api;
export function errorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = error.data as {
      message?: string;
      error?: string;
      errors?: Record<string, string[]>;
    };
    if (data?.errors) return Object.values(data.errors).flat().join(' ');
    if (data?.error === 'contact_in_use') return 'This contact is used by a current or future shift. Update the shift first.';
    if (data?.message) return data.message;
  }
  return 'Unable to save the change. Please try again.';
}
export const memberName = (member?: Member) => member ? `${member.firstName} ${member.lastName}`.trim() : 'Former member';
export const displayTime = (time: string, timezone: string) => new Intl.DateTimeFormat('en-US', {
  timeZone: timezone,
  dateStyle: 'medium',
  timeStyle: 'short'
}).format(new Date(time));
export const localTime = (time: string) => {
  const d = new Date(time);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
export const apiTime = (time: string) => new Date(time).toISOString().replace('.000Z', '+00:00');
